import React, { useState } from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Group } from "@domain/group/group";
import { useAsync } from "@utils/hooks";
import { stack } from "@ui/components/layout";
import { mobile } from "@ui/styles/utils";
import { ShareroomAboutEditor } from "@ui/pages/group/shareroom/shareroomAboutView";
import { ShareroomAdminList } from "@ui/pages/group/shareroom/shareroomAdminList";
import { ShareroomLastFeedback } from "@ui/pages/group/shareroom/shareroomLastFeedback";
import { UnauthorizedAccessShareroom } from "./unauthorizedAccessShareroom";

interface GroupPresentationProps extends React.HTMLAttributes<HTMLDivElement> {
	group: Group;
}

export const GroupPresentation: React.FC<GroupPresentationProps> = ({ group, ...htmlProps }) => {
	const { groupService } = useServices();
	const currentProfile = useCurrentProfile();
	const [shareroom, setShareroom] = useState<Group>({ ...group });

	const { run } = useAsync(async (): Promise<Group> => {
		const newShareroom = await groupService.saveShareroom({ ...shareroom }, shareroom.id);
		return newShareroom;
	}, [shareroom]);

	return (
		<Container {...htmlProps}>
			{group.owner.id !== currentProfile?.id &&
			group.subscriptionStatus === "EXPIRED" &&
			group.license === "PREMIUM" ? (
				<UnauthorizedAccessShareroom shareroom={group} />
			) : null}

			<ShareroomAboutEditor
				onChange={(value, presentationAsset) => {
					if (value) setShareroom((current) => ({ ...current, presentation: value }));
					if (!value && presentationAsset) {
						setShareroom((current) => ({ ...current, presentationAsset: presentationAsset }));
					}
				}}
				onSave={() => run()}
				shareroom={shareroom}
				field="presentation"
				profile={currentProfile}
			/>
			<ShareroomAboutEditor
				onChange={(value) => setShareroom((current) => ({ ...current, objective: value }))}
				onSave={() => run()}
				shareroom={shareroom}
				field="objective"
				profile={currentProfile}
			/>
			<ShareroomAboutEditor
				onChange={(value) => setShareroom((current) => ({ ...current, formatCourse: value }))}
				onSave={() => run()}
				shareroom={shareroom}
				field="formatCourse"
				profile={currentProfile}
			/>
			<Bloc>
				<ShareroomAboutEditor
					onChange={(value) => setShareroom((current) => ({ ...current, schedule: value }))}
					onSave={() => run()}
					shareroom={shareroom}
					field="schedule"
					profile={currentProfile}
				/>
				<ShareroomAboutEditor
					onChange={(value) => setShareroom((current) => ({ ...current, targetAudience: value }))}
					onSave={() => run()}
					shareroom={shareroom}
					field="targetAudience"
					profile={currentProfile}
				/>
			</Bloc>
			<ShareroomLastFeedback shareroom={shareroom} />
			<ShareroomAdminList shareroom={shareroom} profile={currentProfile} />
		</Container>
	);
};

const Container = styled.div`
	${stack(24)}
`;

const Bloc = styled.div`
	width: 100%;
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-template-row: auto;
	gap: 24px;

	@media ${mobile} {
		grid-template-row: 1fr 1fr;
		grid-template-columns: auto;
	}
`;
